import {
  EnhancedSeoHead,
  generateBreadcrumbSchema,
  NotFoundPage,
} from "@appcaire/shared/seo";
import { ExternalLink, ShieldCheck } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useStockholmEventTracking } from "../lib/analytics";
import {
  OFFICIAL_STOCKHOLM_CHOOSER_URL,
  SITE_ORIGIN,
  STOCKHOLM_NOT_FOUND_LINKS,
} from "../lib/site";

interface Section {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
}

interface StaticDefinition {
  title: string;
  /** Search-result title; kept distinct from the visible H1. */
  metaTitle: string;
  description: string;
  eyebrow: string;
  sections: Section[];
}

const PAGES: Record<string, StaticDefinition> = {
  om: {
    title: "Om Stockholms Hemtjänstguide",
    metaTitle: "Om guiden – vem står bakom Stockholms Hemtjänstguide",
    description:
      "Vi hjälper dig som ska välja hemtjänst i Stockholm att jämföra utförare i lugn och ro – med uppgifter från officiella källor.",
    eyebrow: "Om tjänsten",
    sections: [
      {
        heading: "En hjälp när du ska välja hemtjänst",
        paragraphs: [
          "Stockholms Hemtjänstguide är för dig som har fått hemtjänst beviljad och ska välja utförare – och för dig som hjälper en närstående. Här kan du söka, jämföra och spara de utförare du är intresserad av.",
          "Du väljer själv, i lugn och ro. Vi samlar uppgifterna på ett ställe så att du slipper leta på flera håll.",
        ],
      },
      {
        heading: "Vem står bakom guiden?",
        paragraphs: [
          "Guiden drivs av Caire. Vi berättar det öppet så att du vet vem som står bakom och kan bedöma informationen själv.",
        ],
        bullets: [
          "Alla utförare finns med utan kostnad.",
          "Att en utförare är kund hos Caire påverkar inte ordningen i listan.",
          "Betald plats märks tydligt och ändrar inte jämförelsen.",
          "Utförarens egen text hålls skild från uppgifter som kommer från officiella källor.",
        ],
      },
      {
        heading: "Vad du hittar i guiden",
        paragraphs: [
          "Under Hitta hemtjänst finns alla utförare i en lista som du kan söka och filtrera i. Under Stadsdelar ser du vilka utförare som arbetar där du bor. Varje utförare har en egen sida med kontaktuppgifter, språk, särskild kompetens och kvalitetsuppgifter när sådana finns. Under Guider förklarar vi hur du ansöker, vad hemtjänst kostar och hur du byter utförare.",
        ],
        bullets: [
          "Du kan spara upp till tre utförare och jämföra dem sida vid sida.",
          "Uppgifterna om utförarna kommer från Stockholms stads lista över godkända utförare.",
          "Du behöver inget konto för att använda guiden.",
        ],
      },
      {
        heading: "Det här gör vi inte",
        paragraphs: [
          "Vi fattar inga beslut om hemtjänst och avgör inte om en utförare kan ta emot dig – det gör Stockholms stad. Hör med staden eller direkt med utföraren om vad som gäller just nu.",
        ],
      },
    ],
  },
  datakallor: {
    title: "Var uppgifterna kommer ifrån",
    metaTitle:
      "Datakällor – varifrån uppgifterna om hemtjänst i Stockholm kommer",
    description:
      "Vi visar var varje uppgift kommer ifrån – från Stockholms stad, från oberoende mätningar eller från utföraren själv.",
    eyebrow: "Öppet om källorna",
    sections: [
      {
        heading: "Från Stockholms stad",
        paragraphs: [
          "Grunduppgifterna kommer från Stockholms stad: vilka utförare som är godkända, vilka stadsdelar de arbetar i, om de är kommunala eller privata, vilka språk de talar och vilken särskild kompetens de uppger. Vi visar uppgiften när staden har den.",
          "Vi anger när uppgifterna senast hämtades, så att du ser hur aktuella de är.",
        ],
      },
      {
        heading: "Kvalitet från oberoende mätningar",
        paragraphs: [
          "Siffror om kvalitet kommer från offentliga mätningar, till exempel Socialstyrelsen och Kolada. Vi visar alltid vilket år siffran gäller och vad den betyder. Saknas en siffra skriver vi det – vi gissar aldrig och visar den aldrig som noll.",
        ],
        bullets: [
          "Kundnöjdhet visar hur stor andel av de tillfrågade som är nöjda.",
          "Personalkontinuitet visar hur många olika personer som hjälper dig under två veckor – färre är bättre.",
          "Svaren bygger på ett urval, så jämför med sunt förnuft.",
        ],
      },
      {
        heading: "Utförarens egen beskrivning",
        paragraphs: [
          "En del texter är skrivna av utföraren själv – det visar vi tydligt. Är något avgörande för ditt val, fråga utföraren direkt så att du får det bekräftat.",
        ],
      },
    ],
  },
  metodik: {
    title: "Så fungerar guiden",
    metaTitle: "Metodik – så listas och jämförs hemtjänstutförare i Stockholm",
    description:
      "Kort om hur vi listar utförare, i vilken ordning de visas och varför du kan lita på jämförelsen.",
    eyebrow: "Enkelt och ärligt",
    sections: [
      {
        heading: "Alla godkända utförare finns med",
        paragraphs: [
          "Vi listar alla hemtjänstutförare som är godkända i Stockholms stad. Ingen betalar för att komma med, och ingen tas bort för att de inte är kund hos oss.",
        ],
      },
      {
        heading: "Vi visar dem i neutral ordning",
        paragraphs: [
          "Listan står i bokstavsordning. Väljer du att sortera på till exempel nöjdhet ser du alltid varför ordningen ändras.",
        ],
        bullets: [
          "Grundordningen är alltid bokstavsordning.",
          "Betald plats och sponsring märks tydligt och ändrar aldrig ordningen.",
          "Saknas uppgifter för att jämföra hamnar utföraren sist – aldrig dold.",
        ],
      },
      {
        heading: "Så hamnar en utförare i en stadsdel",
        paragraphs: [
          "En utförare visas i de stadsdelar där Stockholms stad anger att den arbetar. Samma utförare kan därför finnas i flera stadsdelar. Att en utförare finns i din stadsdel betyder inte alltid att den har plats för nya kunder just nu – ring och fråga om de kan börja på din adress och vid de tider du behöver.",
        ],
      },
      {
        heading: "CaireIndex är ett separat jämförelsestöd",
        paragraphs: [
          "Vid varje utförare visas CaireIndex, ett samlat mått som räknas fram ur offentliga kvalitetsuppgifter. Bredvid värdet ser du hur fullständigt underlaget är. Indexet ändrar inte den förvalda bokstavsordningen, och hela beräkningen finns beskriven på Sveriges Hemtjänsts sida om metodik.",
        ],
      },
      {
        heading: "Siffrorna kommer från officiella källor",
        paragraphs: [
          'Uppgifter om kvalitet kommer från Stockholms stad och oberoende mätningar – inte från utförarna själva. Saknas en uppgift skriver vi "Uppgift saknas" i stället för att gissa. Ser något fel ut? Hör av dig, så rättar vi det mot källan hos Stockholms stad.',
        ],
      },
    ],
  },
  integritet: {
    title: "Integritet",
    metaTitle: "Integritet – så hanteras dina uppgifter i hemtjänstguiden",
    description:
      "Din kortlista stannar i din webbläsare, och vi samlar inte in några uppgifter om din hälsa.",
    eyebrow: "Din information",
    sections: [
      {
        heading: "Din lista stannar i din webbläsare",
        paragraphs: [
          "De utförare du sparar finns bara i din egen webbläsare. Du behöver inget konto. Delar du din lista skickas bara vilka utförare du valt – aldrig något om dig, din kontakt eller din hälsa.",
        ],
      },
      {
        heading: "Anonym statistik",
        paragraphs: [
          "Vi räknar hur guiden används – till exempel hur många som besöker en sida eller sparar en utförare – för att göra den bättre. Statistiken är anonym och innehåller aldrig namn, telefonnummer, e-post eller något om din hälsa.",
        ],
      },
      {
        heading: "Om du kontaktar en utförare",
        paragraphs: [
          "Om du fyller i ett kontaktformulär samlar vi bara in det som behövs för att skicka din förfrågan: dina kontaktuppgifter, vilken kommun det gäller, vad du är intresserad av och vilken utförare det rör. Skriv inte diagnoser eller andra känsliga uppgifter om din hälsa.",
          "Dina kontaktuppgifter hålls åtskilda från den anonyma statistiken.",
        ],
      },
      {
        heading: "Det här sparas i din webbläsare",
        paragraphs: [
          "Kortlistan sparas i webbläsarens lokala lagring på din egen dator eller telefon. Har du valt var du befinner dig – till exempel att du ska byta utförare – sparas även det valet där, så att guiden kan visa rätt vägledning nästa gång. Rensar du webbläsarens data försvinner båda.",
        ],
      },
      {
        heading: "Rensa och dela",
        paragraphs: [
          "Du kan rensa din lista när du vill. Delar du en länk är det mottagaren som väljer att öppna den och spara den i sin egen webbläsare.",
        ],
      },
    ],
  },
  "for-utforare": {
    title: "För hemtjänstutförare i Stockholm",
    metaTitle:
      "För utförare – så håller ni hemtjänstprofilen i Stockholm aktuell",
    description:
      "Din verksamhet finns redan med. Så håller du profilen rätt och ser intresset från besökare via Mitt Caire.",
    eyebrow: "För dig som driver hemtjänst",
    sections: [
      {
        heading: "Din profil är gratis",
        paragraphs: [
          "Alla godkända hemtjänstutförare i Stockholm finns med utan kostnad. Uppgifter från Stockholms stad står kvar som de är och kan inte ändras med reklam.",
        ],
      },
      {
        heading: "Ta över din profil",
        paragraphs: [
          "I Mitt Caire kan du som är behörig ta över din utförares profil, fylla på med egen text och se hur många som tittar på profilen, sparar den och hör av sig.",
        ],
        bullets: [
          "Rätta felaktiga officiella uppgifter hos Stockholms stad.",
          "Beskriv hur ni arbetar – utan löften ni inte kan hålla.",
          "Ange de språk och den kompetens ni faktiskt kan bemanna.",
          "Håll kontaktuppgifter och vilka stadsdelar ni arbetar i aktuella.",
        ],
      },
      {
        heading: "Varifrån uppgifterna i profilen kommer",
        paragraphs: [
          "Namn, stadsdelar, kommunal eller privat regi, språk och särskild kompetens hämtas från Stockholms stads lista över godkända utförare. Kvalitetsuppgifter kommer från offentliga mätningar, till exempel Socialstyrelsen och Kolada. Stämmer något inte ska uppgiften i första hand rättas hos Stockholms stad – då uppdateras den även här.",
        ],
      },
      {
        heading: "Sponsring och Caire",
        paragraphs: [
          "Vill ni synas mer märks det alltid tydligt som betald plats, och det påverkar aldrig ordningen i listan. Caire erbjuder också verksamhetsstöd för hemtjänst – men det hålls helt åtskilt från jämförelsen som besökarna ser.",
        ],
      },
    ],
  },
};

const RELATED_LINKS: ReadonlyArray<{
  to: string;
  label: string;
  description: string;
}> = [
  {
    to: "/hitta-hemtjanst",
    label: "Hitta hemtjänst i Stockholm",
    description: "Alla godkända utförare i en lista som du kan filtrera.",
  },
  {
    to: "/stadsdelar",
    label: "Hemtjänst per stadsdel",
    description: "Se vilka utförare som arbetar där du bor.",
  },
  {
    to: "/guider",
    label: "Guider om hemtjänst",
    description: "Ansöka, välja, jämföra och byta utförare.",
  },
  ...Object.entries(PAGES).map(([slug, definition]) => ({
    to: `/${slug}`,
    label: definition.title,
    description: definition.description,
  })),
];

export default function StaticPage() {
  const { page = "" } = useParams<{ page: string }>();
  const trackStockholmEvent = useStockholmEventTracking();
  const definition = PAGES[page];
  if (!definition) {
    return <NotFoundPage navigationLinks={STOCKHOLM_NOT_FOUND_LINKS} />;
  }
  const canonicalUrl = `${SITE_ORIGIN}/${page}`;
  return (
    <>
      <EnhancedSeoHead
        siteKey="stockholm"
        title={definition.metaTitle}
        description={definition.description}
        canonicalUrl={canonicalUrl}
        contentClassification="Consumer information"
        targetAudience="seniors, relatives, home-care providers"
        schemaMarkup={generateBreadcrumbSchema([
          { name: "Hem", url: SITE_ORIGIN },
          { name: definition.title, url: canonicalUrl },
        ])}
      />
      <article>
        <header className="border-b border-border bg-accent/65">
          <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:px-8">
            <nav
              aria-label="Brödsmulor"
              className="mb-4 flex flex-wrap items-center gap-2 text-sm text-muted-foreground"
            >
              <Link to="/" className="hover:text-brand-primary hover:underline">
                Hem
              </Link>
              <span aria-hidden="true">/</span>
              <span className="text-foreground">{definition.title}</span>
            </nav>
            <p className="text-sm font-bold uppercase tracking-wider text-brand-secondary">
              {definition.eyebrow}
            </p>
            <h1 className="mt-3 font-[var(--font-editorial)] text-4xl font-bold text-foreground md:text-5xl">
              {definition.title}
            </h1>
            <p className="mt-5 text-xl leading-9 text-muted-foreground">
              {definition.description}
            </p>
          </div>
        </header>
        <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="space-y-10">
            {definition.sections.map((section) => (
              <section key={section.heading}>
                <h2 className="text-2xl font-bold text-foreground md:text-3xl">
                  {section.heading}
                </h2>
                {section.paragraphs.map((paragraph) => (
                  <p
                    key={paragraph}
                    className="mt-4 text-lg leading-8 text-muted-foreground"
                  >
                    {paragraph}
                  </p>
                ))}
                {section.bullets && (
                  <ul className="mt-5 space-y-3">
                    {section.bullets.map((bullet) => (
                      <li
                        key={bullet}
                        className="flex gap-3 text-lg leading-8 text-muted-foreground"
                      >
                        <ShieldCheck
                          className="mt-1 h-5 w-5 shrink-0 text-brand-primary"
                          aria-hidden="true"
                        />{" "}
                        {bullet}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>

          {page === "datakallor" && (
            <a
              href={OFFICIAL_STOCKHOLM_CHOOSER_URL}
              target="_blank"
              rel="noreferrer"
              className="mt-10 inline-flex min-h-11 items-center gap-2 font-semibold text-brand-primary underline underline-offset-4"
            >
              Stockholms officiella utförarlista{" "}
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
            </a>
          )}
          {page === "for-utforare" && (
            <div className="mt-10 grid gap-4 rounded-3xl bg-brand-primary p-7 text-white sm:grid-cols-2">
              <a
                href="https://sverigeshemtjanst.se/mittcaire?municipality=stockholm&source=hemtjanstistockholm"
                onClick={() =>
                  trackStockholmEvent("seo_funnel_click", {
                    source_surface: "guide",
                    target_path: "/mittcaire",
                  })
                }
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 font-bold text-brand-primary"
              >
                Öppna Mitt Caire{" "}
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href="https://www.caire.se"
                onClick={() =>
                  trackStockholmEvent("seo_funnel_click", {
                    source_surface: "guide",
                    target_path: "/",
                  })
                }
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-white/40 px-5 py-3 font-bold text-white"
              >
                Se Caires produkter{" "}
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          )}
          <nav
            className="mt-12 border-t border-border pt-8"
            aria-label="Mer om guiden"
          >
            <h2 className="text-xl font-bold text-foreground">Mer om guiden</h2>
            <ul className="mt-4 grid gap-4 sm:grid-cols-2">
              {RELATED_LINKS.filter((link) => link.to !== `/${page}`).map(
                (link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="inline-flex min-h-11 items-center font-semibold text-brand-primary underline underline-offset-4"
                    >
                      {link.label}
                    </Link>
                    <p className="mt-1 text-muted-foreground">
                      {link.description}
                    </p>
                  </li>
                ),
              )}
            </ul>
          </nav>
        </div>
      </article>
    </>
  );
}
